import { type CSSProperties, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";

export interface StripItem {
  /** Stable identity — an image id, or a device mxid for the camera strip. */
  key: string;
  /** Thumbnail source. Null renders the slot without a picture rather than hiding it. */
  src: string | null;
  /** Short caption under/over the thumb: a frame time, or a camera name. */
  label: string;
  /** Marks the frame the detection actually fired on. */
  isAlertFrame?: boolean;
}

interface AlertReviewStripProps {
  items: StripItem[];
  activeIndex: number;
  onSelect: (index: number) => void;
  /** Screen-reader name — the two strips carry different meanings. */
  label: string;
  /** Which edge the strip hugs; only affects the rounding of the scroll shadow. */
  side: "left" | "right";
  className?: string;
  /** The strip is as tall as the panel it belongs to. */
  style?: CSSProperties;
}

/**
 * The vertical strip beside a review panel.
 *
 * One component for BOTH strips — the event's frames on the alert side, the
 * location's cameras on the video side — because they are the same gesture:
 * a column of stills you step through to change what the big panel shows. Giving
 * them separate implementations is how the two sides drift apart.
 */
export const AlertReviewStrip = ({
  items,
  activeIndex,
  onSelect,
  label,
  side,
  className,
  style,
}: AlertReviewStripProps) => {
  const { t } = useTranslation("map");
  const listRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Keep the selection in view when it moves by keyboard rather than by click —
  // stepping frames with ↑/↓ is the primary way through a long event.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const step = (delta: number) => {
    if (items.length === 0) return;
    onSelect(Math.min(Math.max(activeIndex + delta, 0), items.length - 1));
  };

  if (items.length === 0) return null;

  return (
    <div
      className={cn("flex w-24 shrink-0 flex-col gap-1.5", className)}
      style={style}
      role="listbox"
      aria-label={label}
      aria-orientation="vertical"
    >
      <StripArrow
        direction="up"
        disabled={activeIndex <= 0}
        onClick={() => step(-1)}
        label={t("alertReview.strip.previous", { label })}
      />

      <div
        ref={listRef}
        className="hide-scrollbar flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-0.5"
      >
        {items.map((item, index) => {
          const active = index === activeIndex;
          return (
            <button
              key={item.key}
              ref={active ? activeRef : undefined}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => onSelect(index)}
              title={item.label}
              className={cn(
                "relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-md border bg-black",
                "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                active ? "border-amber-400" : "border-border/60 hover:border-border",
              )}
            >
              {item.src ? (
                <img src={item.src} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : null}
              <span
                className={cn(
                  "absolute inset-x-0 bottom-0 truncate bg-black/65 px-1 py-0.5 text-left",
                  "font-mono text-[10px] leading-tight text-white",
                )}
              >
                {item.label}
              </span>
              {item.isAlertFrame ? (
                // The frame the model fired on, so it stays findable in a long event.
                <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-amber-400" />
              ) : null}
            </button>
          );
        })}
      </div>

      <StripArrow
        direction="down"
        disabled={activeIndex >= items.length - 1}
        onClick={() => step(1)}
        label={t("alertReview.strip.next", { label })}
      />

      <div
        className={cn(
          "rounded-md border border-border/60 bg-background/80 py-0.5 text-center",
          "font-mono text-[11px] tabular-nums text-muted-foreground",
          side === "left" ? "" : "",
        )}
      >
        {activeIndex + 1} / {items.length}
      </div>
    </div>
  );
};

const StripArrow = ({
  direction,
  disabled,
  onClick,
  label,
}: {
  direction: "up" | "down";
  disabled: boolean;
  onClick: () => void;
  label: string;
}) => {
  const Icon = direction === "up" ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "flex h-6 w-full items-center justify-center rounded-md border border-border/60",
        "bg-background/80 text-muted-foreground transition-colors",
        "hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
};

export default AlertReviewStrip;
